import { supabase } from './supabase';

import type { ApiResponse } from './appConfigService';

export interface AnalyticsSectionDefinition {
  id: string;
  label: string;
  view: string;
  description?: string;
}

export interface AnalyticsSectionData extends AnalyticsSectionDefinition {
  data: Record<string, unknown> | null;
  error?: string;
}

export const ANALYTICS_SECTIONS: AnalyticsSectionDefinition[] = [
  {
    id: 'overview',
    label: 'Overview',
    view: 'analytics_overview',
    description: 'DAU/MAU, new users, retention, and sessions.',
  },
  {
    id: 'content',
    label: 'Content',
    view: 'analytics_content',
    description: 'Sermons, devotionals, and announcements.',
  },
  {
    id: 'devotionals-detail',
    label: 'Devotionals Detail',
    view: 'analytics_devotionals_detail',
    description: 'Completion rates, top series, and recent devotional activity.',
  },
  {
    id: 'community',
    label: 'Community',
    view: 'analytics_community',
    description: 'Posts, comments, likes, and groups.',
  },
  {
    id: 'events',
    label: 'Events',
    view: 'analytics_events',
    description: 'Registrations, attendance, and capacity.',
  },
  {
    id: 'kids-church',
    label: 'Kids Church',
    view: 'analytics_kids_church',
    description: 'Check-ins, active children, and staff coverage.',
  },
  {
    id: 'volunteer',
    label: 'Volunteer',
    view: 'analytics_volunteer',
    description: 'Requests, opportunities, and fill rate.',
  },
  {
    id: 'moderation',
    label: 'Moderation',
    view: 'analytics_moderation',
    description: 'Reports volume and resolution time.',
  },
];

const fetchSection = async (
  section: AnalyticsSectionDefinition
): Promise<AnalyticsSectionData> => {
  const { data, error } = await supabase
    .from(section.view)
    .select('*')
    .limit(1)
    .maybeSingle();

  return {
    ...section,
    data: data ?? null,
    error: error?.message,
  };
};

export const analyticsService = {
  async getDashboardSections(): Promise<ApiResponse<AnalyticsSectionData[]>> {
    try {
      const results = await Promise.all(ANALYTICS_SECTIONS.map(fetchSection));
      const hasErrors = results.some((section) => section.error);

      return {
        success: !hasErrors,
        data: results,
        message: hasErrors
          ? 'Some analytics sections failed to load. Check view permissions and definitions.'
          : undefined,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to load analytics.',
      };
    }
  },
};
